import { action } from '@ember/object';
import { next, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import PanelEntryPoint, {
	type PanelEntryPointProps,
} from '@databox/ember-integrations/lib/components/Ai/Panel/PanelEntryPoint';
import { ContextObjectType } from '@databox/types/domains/ai/context.enums';

import type { ReactIntegrations, Service } from 'webapp-types';
import { GenieBookACallOrigin } from 'webapp/enums/ai-chat';
import { AppEvent } from 'webapp/enums/events';
import { TourId } from 'webapp/enums/tour';
import { getShadowDomMode } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

interface GeniePanelSignature {
	Element: HTMLDivElement;
	Args: {
		hasLauncher: boolean;
	};
}

export default class GeniePanelComponent extends Component<GeniePanelSignature> {
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('leave-guard') declare leaveGuardService: Service.LeaveGuard;
	@service('dataset-events') declare datasetEventsService: Service.DatasetEvents;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('react') declare reactService: Service.React;
	@service('tour') declare tourService: Service.Tour;

	reactElement: ReactIntegrations.Root = null;

	@action
	setupView(node: HTMLElement) {
		this.reactElement = this.reactService.create(node as HTMLDivElement);

		// Render the panel component after the initial render (lazy loaded component)
		schedule('afterRender', () => {
			next(() => {
				this.renderPanel();
			});
		});

		this.eventManagerService.on(AppEvent.ACCESS_TOKEN_REFRESHED, this, this.renderPanel);
		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderPanel);
	}

	@action
	teardownView() {
		this.reactService.unmount(this.reactElement);
		// A remounted panel starts from its own state; it must not replay a conversation it already started.
		this.aiChatService.panelConversation = null;
		this.eventManagerService.off(AppEvent.ACCESS_TOKEN_REFRESHED, this);
		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
	}

	get isOpen(): boolean {
		return this.aiChatService.isOpen;
	}

	get panelConversation(): Service.AIChat['panelConversation'] {
		return this.aiChatService.panelConversation;
	}

	get currentSpaceAccessId(): string | null {
		return this.aiChatService.currentSpaceAccessId;
	}

	// The panel stays open across routes, so the origin is re-read on navigation
	get trackingOrigin() {
		return this.aiChatService.trackingOrigin;
	}

	// Outside the dataset routes the panel opens without a context item
	get contextItems() {
		if (!this.currentSpaceAccessId) {
			return [];
		}

		return [
			{
				objectType: ContextObjectType.SPACE_ACCESS,
				objectTypeId: Number(this.currentSpaceAccessId),
			},
		];
	}

	@action
	renderPanel() {
		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		const props: PanelEntryPointProps = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			accessToken: accessToken!,
			dataToken: dataToken!,
			spaceId: this.accountService.id,
			user: {
				id: Number(this.currentUserService.id),
				name: this.currentUserService.displayName,
			},
			isOpen: this.isOpen,
			onClose: () => this.aiChatService.onUserClose(),
			hasLauncher: this.args.hasLauncher,
			contextItems: this.panelConversation?.contextItems ?? this.contextItems,
			trackingOrigin: this.trackingOrigin,
			onActionClick: this.aiChatService.onActionClick,
			showToast: this.aiChatService.showToast,
			// `onActiveThreadChange` is deliberately NOT passed here. It writes the URL-synced thread id, which the
			// nav sidebar highlights off — and this panel opens on non-genie routes, so wiring it would let a thread
			// opened here highlight a row while the user is elsewhere. Only the full-screen genie reports it.
			onGetThreadId: this.aiChatService.onGetThreadId,
			onNavigateToSettings: this.aiChatService.onNavigateToSettings,
			onNavigateToRoutine: this.aiChatService.onNavigateToRoutine,
			onNavigateToGenie: this.aiChatService.openInGenie,
			initialMessages: this.panelConversation?.initialMessages,
			hostKey: this.panelConversation?.key,
			onBookACall: this.aiChatService.hasBookingTarget
				? () => this.aiChatService.onBookACall(GenieBookACallOrigin.SIDE_PANEL)
				: undefined,
			offersBookACall: this.aiChatService.canBookACall,
			supportEmail: this.aiChatService.supportEmail,
			slack: this.aiChatService.slackHostProps,
			onUpgrade: this.aiChatService.onUpgrade,
			onAddCredits: this.aiChatService.onAddCredits,
			featureConfig: this.aiChatService.featureConfig,
			subscribeToAiEvents: this.aiChatService.subscribeToAiEvents,
			tour: this.tourService.getGenieTourConfig([TourId.GENIE_ARTIFACT_EDIT]),
			// `isOpen` re-renders the panel on open and close, so a hidden panel's pending artifact work stops holding
			// the app's navigation behind a dialog nobody can see.
			registerLeaveGuard: this.leaveGuardService.register,
			registerShownDataset: this.datasetEventsService.registerShownDataset,
		};

		if (this.reactElement) {
			this.reactService.render(
				this.reactElement,
				PanelEntryPoint(props),
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	<template>
		{{#if this.aiChatService.hasBeenOpened}}
			{{! prettier-ignore }}
			<div
				{{didInsert this.setupView}}
				{{didUpdate this.renderPanel this.isOpen this.currentSpaceAccessId this.panelConversation @hasLauncher this.trackingOrigin}}
				{{willDestroy this.teardownView}}
				class={{this.aiChatService.genieLayer}}
				...attributes
			/>
		{{/if}}
	</template>
}
